import React, { useState, useEffect } from 'react';
import DashboardLayout from '../components/Layout/DashboardLayout';
import { Helmet } from 'react-helmet';
import FAQSection from '../components/FAQSection';

const LocalRankings = () => {
  const [activeTab, setActiveTab] = useState('overview');
  const [domain, setDomain] = useState('');
  const [location, setLocation] = useState('');
  const [keywords, setKeywords] = useState('');
  const [loading, setLoading] = useState(false);
  const [rankData, setRankData] = useState(null);
  const [error, setError] = useState(null);

  const handleTrack = async () => {
    if (!domain || !location) {
      setError('Please enter a domain and location');
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem('token');
      const kws = keywords.split('\n').filter(k => k.trim());
      const res = await fetch('/api/local-seo/rankings', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': 'Bearer ' + token },
        body: JSON.stringify({ domain, location, keywords: kws })
      });
      const data = await res.json();
      if (data.success) {
        setRankData(data.data);
      } else {
        setError(data.error || 'Failed to fetch rankings');
      }
    } catch (err) {
      setError('Network error');
    }
    setLoading(false);
  };

  const cardStyle = {
    background: '#fff', borderRadius: '12px', padding: '24px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.08)', marginBottom: '20px'
  };
  const statCard = (label, value, color, sub) => (
    <div style={{...cardStyle, textAlign: 'center', flex: 1, minWidth: '200px'}}>
      <div style={{fontSize: '14px', color: '#666', marginBottom: '8px'}}>{label}</div>
      <div style={{fontSize: '32px', fontWeight: 700, color: color || '#1a73e8'}}>{value}</div>
      {sub && <div style={{fontSize: '12px', color: '#999', marginTop: '4px'}}>{sub}</div>}
    </div>
  );

  const tabBtn = (id, label) => (
    <button onClick={() => setActiveTab(id)} style={{
      padding: '10px 20px', border: 'none', borderBottom: activeTab === id ? '3px solid #1a73e8' : '3px solid transparent',
      background: 'none', color: activeTab === id ? '#1a73e8' : '#666',
      fontWeight: activeTab === id ? 600 : 400, cursor: 'pointer', fontSize: '14px'
    }}>{label}</button>
  );

  const getRatingColor = (pos) => {
    if (!pos || pos > 20) return '#dc3545';
    if (pos <= 3) return '#28a745';
    if (pos <= 10) return '#ffc107';
    return '#fd7e14';
  };


  const LOCAL_FAQ = [
    { q: "What is local rank tracking and how does it differ from regular rank tracking?", a: "Local rank tracking monitors your search positions for location-specific queries at granular geographic levels like cities, zip codes, and neighborhoods. Unlike regular rank tracking which shows national or global positions, local tracking reveals how your business ranks in the specific areas you serve. This is critical because search results vary dramatically by location, especially for service-based businesses." },
    { q: "How does SERPfinity track local rankings?", a: "SERPfinity tracks local rankings by simulating searches from specific geographic coordinates, monitoring organic positions, Google Maps local pack appearances, and local finder results. We track daily changes across multiple locations simultaneously, compare your rankings against local competitors, and alert you when significant position changes occur in your target service areas." },
    { q: "Why do my rankings differ by location?", a: "Google personalizes search results based on the searcher's physical location, especially for queries with local intent. A pizza restaurant might rank first in searches from nearby neighborhoods but not appear at all for searches 10 miles away. SERPfinity helps you understand your ranking radius and identify areas where you need to improve local SEO signals like citations, reviews, and content." },
    { q: "How can I improve my local search rankings?", a: "Improving local rankings requires optimizing your Google Business Profile with complete information, building consistent NAP citations across directories, earning positive customer reviews, creating location-specific content, building local backlinks, implementing local schema markup, and maintaining an active social media presence. SERPfinity tracks all these signals and provides prioritized recommendations." },
    { q: "What is the Google Local Pack and how do I rank in it?", a: "The Google Local Pack is the map-based section showing three business listings that appears at the top of search results for local queries. Ranking in the Local Pack requires a verified Google Business Profile, strong review signals, NAP consistency, proximity to the searcher, category relevance, and robust citation profiles. SERPfinity monitors your Local Pack visibility and tracks the specific factors affecting your placement." },
    { q: "How does local SEO connect to AI search for local businesses?", a: "AI assistants increasingly handle local business queries like finding restaurants or plumbers nearby. These AI systems pull from the same data sources as traditional local search including Google Business Profile, reviews, and directory listings. SERPfinity tracks how AI platforms recommend your business for local queries and helps optimize your local presence for both traditional and AI-powered local discovery." }
  ];

  return (
    <>
      <Helmet>
        <title>Local Rank Tracker | Monitor Local SEO Rankings | SERPfinity</title>
        <meta name="description" content="Track local search rankings at city, zip code, and neighborhood level. Monitor Google Maps positions, local pack appearances, and competitor movements with SERPfinity local rank tracker." />
        <meta name="keywords" content="local rank tracker, local SEO rankings, Google Maps ranking, local pack tracker, geo-specific rankings, local search monitor, neighborhood ranking tracker, SERPfinity" />
        <link rel="canonical" href="https://thedigitalhall.club/local-rankings" />
        <meta property="og:title" content="Local Rank Tracker | SERPfinity" />
        <meta property="og:description" content="Track local search rankings by city, zip code, and neighborhood. Monitor Google Maps and local pack positions." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://thedigitalhall.club/local-rankings" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Local Rank Tracker | SERPfinity" />
        <meta name="twitter:description" content="Monitor local SEO rankings across locations. Track Google Maps positions and local pack appearances." />
        <meta name="robots" content="index, follow" />
        <script type="application/ld+json">{JSON.stringify({"@context":"https://schema.org","@type":"FAQPage","mainEntity":LOCAL_FAQ.map(item=>({"@type":"Question","name":item.q,"acceptedAnswer":{"@type":"Answer","text":item.a}}))})}</script>
      </Helmet>
    <div style={{padding: '30px', maxWidth: '1200px', margin: '0 auto'}}>
      <div style={{marginBottom: '30px'}}>
        <h1 style={{fontSize: '28px', fontWeight: 700, color: '#1a1a2e', margin: 0}}>Local Rankings Monitor</h1>
        <p style={{color: '#666', marginTop: '8px'}}>Track your visibility for location-based keywords in Google Search and Maps</p>
      </div>

      <div style={cardStyle}>
        <h3 style={{margin: '0 0 16px', fontSize: '16px', color: '#333'}}>Track Local Keywords</h3>
        <div style={{display: 'flex', gap: '12px', flexWrap: 'wrap', alignItems: 'flex-end'}}>
          <div style={{flex: 1, minWidth: '200px'}}>
            <label style={{display: 'block', fontSize: '13px', color: '#666', marginBottom: '4px'}}>Domain</label>
            <input value={domain} onChange={e => setDomain(e.target.value)} placeholder="yourbusiness.com"
              style={{width: '100%', padding: '10px 14px', border: '1px solid #ddd', borderRadius: '8px', fontSize: '14px', boxSizing: 'border-box'}} />
          </div>
          <div style={{flex: 1, minWidth: '200px'}}>
            <label style={{display: 'block', fontSize: '13px', color: '#666', marginBottom: '4px'}}>Location</label>
            <input value={location} onChange={e => setLocation(e.target.value)} placeholder="Brooklyn, NY"
              style={{width: '100%', padding: '10px 14px', border: '1px solid #ddd', borderRadius: '8px', fontSize: '14px', boxSizing: 'border-box'}} />
          </div>
          <div style={{flex: 2, minWidth: '250px'}}>
            <label style={{display: 'block', fontSize: '13px', color: '#666', marginBottom: '4px'}}>Keywords (one per line)</label>
            <textarea value={keywords} onChange={e => setKeywords(e.target.value)} rows={3}
              placeholder={"bakery in Brooklyn\ncoffee shop near me\nbest pizza Brooklyn NY"}
              style={{width: '100%', padding: '10px 14px', border: '1px solid #ddd', borderRadius: '8px', fontSize: '14px', boxSizing: 'border-box', resize: 'vertical'}} />
          </div>
          <button onClick={handleTrack} disabled={loading} style={{
            padding: '12px 28px', background: loading ? '#ccc' : '#1a73e8', color: '#fff',
            border: 'none', borderRadius: '8px', cursor: loading ? 'default' : 'pointer',
            fontWeight: 600, fontSize: '14px', height: 'fit-content'
          }}>{loading ? 'Tracking...' : 'Track Rankings'}</button>
        </div>
        {error && <div style={{color: '#dc3545', marginTop: '12px', fontSize: '14px'}}>{error}</div>}
      </div>

      {rankData && (
        <>
          <div style={{display: 'flex', gap: '16px', flexWrap: 'wrap', marginBottom: '20px'}}>
            {statCard('Keywords Tracked', rankData.summary.totalKeywords, '#1a73e8')}
            {statCard('Avg. Position', rankData.summary.avgPosition, getRatingColor(rankData.summary.avgPosition), 'Google Search')}
            {statCard('Top 3 Rankings', rankData.summary.top3, '#28a745')}
            {statCard('Local Pack', rankData.summary.localPackCount + '/' + rankData.summary.totalKeywords, '#6f42c1', 'Appearances')}
          </div>

          <div style={{...cardStyle, padding: 0, overflow: 'hidden'}}>
            <div style={{display: 'flex', borderBottom: '1px solid #eee', padding: '0 16px'}}>
              {tabBtn('overview', 'Overview')}
              {tabBtn('serp', 'SERP Rankings')}
              {tabBtn('localpack', 'Local Pack / Maps')}
              {tabBtn('trends', 'Trends')}
            </div>

            <div style={{padding: '24px'}}>
              {activeTab === 'overview' && (
                <div>
                  <h3 style={{margin: '0 0 16px', fontSize: '16px'}}>Ranking Distribution</h3>
                  <div style={{display: 'flex', gap: '12px', marginBottom: '24px', flexWrap: 'wrap'}}>
                    {[
                      {label: 'Position 1-3', count: rankData.summary.top3, color: '#28a745', pct: Math.round(rankData.summary.top3/rankData.summary.totalKeywords*100)},
                      {label: 'Position 4-10', count: rankData.summary.top10 - rankData.summary.top3, color: '#ffc107', pct: Math.round((rankData.summary.top10-rankData.summary.top3)/rankData.summary.totalKeywords*100)},
                      {label: 'Position 11-20', count: rankData.summary.top20 - rankData.summary.top10, color: '#fd7e14', pct: Math.round((rankData.summary.top20-rankData.summary.top10)/rankData.summary.totalKeywords*100)},
                      {label: 'Position 20+', count: rankData.summary.totalKeywords - rankData.summary.top20, color: '#dc3545', pct: Math.round((rankData.summary.totalKeywords-rankData.summary.top20)/rankData.summary.totalKeywords*100)}
                    ].map((item, i) => (
                      <div key={i} style={{flex: 1, minWidth: '140px', padding: '16px', background: '#f8f9fa', borderRadius: '8px', textAlign: 'center'}}>
                        <div style={{width: '100%', height: '8px', background: '#e9ecef', borderRadius: '4px', marginBottom: '8px'}}>
                          <div style={{width: item.pct + '%', height: '100%', background: item.color, borderRadius: '4px'}}></div>
                        </div>
                        <div style={{fontSize: '20px', fontWeight: 700, color: item.color}}>{item.count}</div>
                        <div style={{fontSize: '12px', color: '#666'}}>{item.label} ({item.pct}%)</div>
                      </div>
                    ))}
                  </div>
                  <h3 style={{margin: '0 0 12px', fontSize: '16px'}}>Local vs Organic Split</h3>
                  <div style={{display: 'flex', gap: '20px'}}>
                    <div style={{flex: 1, padding: '20px', background: '#e8f5e9', borderRadius: '8px', textAlign: 'center'}}>
                      <div style={{fontSize: '28px', fontWeight: 700, color: '#2e7d32'}}>{rankData.summary.localPackCount}</div>
                      <div style={{color: '#2e7d32', fontSize: '14px'}}>Local Pack / Maps</div>
                    </div>
                    <div style={{flex: 1, padding: '20px', background: '#e3f2fd', borderRadius: '8px', textAlign: 'center'}}>
                      <div style={{fontSize: '28px', fontWeight: 700, color: '#1565c0'}}>{rankData.summary.organicOnly}</div>
                      <div style={{color: '#1565c0', fontSize: '14px'}}>Organic Only</div>
                    </div>
                  </div>
                </div>
              )}

              {activeTab === 'serp' && (
                <div>
                  <h3 style={{margin: '0 0 16px', fontSize: '16px'}}>Google Search Rankings</h3>
                  <div style={{overflowX: 'auto'}}>
                    <table style={{width: '100%', borderCollapse: 'collapse', fontSize: '14px'}}>
                      <thead>
                        <tr style={{background: '#f8f9fa'}}>
                          <th style={{padding: '12px', textAlign: 'left', borderBottom: '2px solid #dee2e6'}}>Keyword</th>
                          <th style={{padding: '12px', textAlign: 'center', borderBottom: '2px solid #dee2e6'}}>Position</th>
                          <th style={{padding: '12px', textAlign: 'left', borderBottom: '2px solid #dee2e6'}}>URL</th>
                          <th style={{padding: '12px', textAlign: 'center', borderBottom: '2px solid #dee2e6'}}>Search Volume</th>
                          <th style={{padding: '12px', textAlign: 'center', borderBottom: '2px solid #dee2e6'}}>Local Pack</th>
                          <th style={{padding: '12px', textAlign: 'center', borderBottom: '2px solid #dee2e6'}}>Competition</th>
                        </tr>
                      </thead>
                      <tbody>
                        {rankData.rankings.map((r, i) => (
                          <tr key={i} style={{borderBottom: '1px solid #eee'}}>
                            <td style={{padding: '12px', fontWeight: 500}}>{r.keyword}</td>
                            <td style={{padding: '12px', textAlign: 'center'}}>
                              <span style={{display: 'inline-block', padding: '4px 12px', borderRadius: '20px',
                                background: getRatingColor(r.position) + '20', color: getRatingColor(r.position), fontWeight: 600}}>
                                {r.position || 'N/R'}
                              </span>
                            </td>
                            <td style={{padding: '12px', color: '#1a73e8', maxWidth: '250px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'}}>{r.url || '-'}</td>
                            <td style={{padding: '12px', textAlign: 'center'}}>{r.searchVolume ? r.searchVolume.toLocaleString() : '-'}</td>
                            <td style={{padding: '12px', textAlign: 'center'}}>
                              {r.inLocalPack ? <span style={{color: '#28a745', fontWeight: 600}}>Yes</span> : <span style={{color: '#999'}}>No</span>}
                            </td>
                            <td style={{padding: '12px', textAlign: 'center'}}>{r.competition || '-'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {activeTab === 'localpack' && (
                <div>
                  <h3 style={{margin: '0 0 16px', fontSize: '16px'}}>Google Maps / Local Pack Results</h3>
                  {rankData.rankings.filter(r => r.localPackData).length === 0 ? (
                    <p style={{color: '#666', textAlign: 'center', padding: '40px'}}>No Local Pack data found for tracked keywords</p>
                  ) : (
                    rankData.rankings.filter(r => r.localPackData).map((r, i) => (
                      <div key={i} style={{padding: '20px', background: '#f8f9fa', borderRadius: '8px', marginBottom: '16px'}}>
                        <div style={{fontWeight: 600, fontSize: '15px', marginBottom: '12px', color: '#333'}}>
                          "{r.keyword}" <span style={{color: '#999', fontWeight: 400}}>- Local Pack</span>
                        </div>
                        <div style={{display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '12px'}}>
                          {r.localPackData.map((lp, j) => (
                            <div key={j} style={{background: '#fff', padding: '16px', borderRadius: '8px', border: '1px solid #e0e0e0'}}>
                              <div style={{display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px'}}>
                                <span style={{background: lp.isTarget ? '#1a73e8' : '#e9ecef', color: lp.isTarget ? '#fff' : '#666',
                                  width: '28px', height: '28px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
                                  fontWeight: 700, fontSize: '13px'}}>{lp.rank}</span>
                                <span style={{fontWeight: 600, color: lp.isTarget ? '#1a73e8' : '#333'}}>{lp.title}</span>
                              </div>
                              <div style={{fontSize: '13px', color: '#666', marginBottom: '4px'}}>{lp.address || 'No address'}</div>
                              <div style={{display: 'flex', gap: '12px', fontSize: '12px', color: '#999'}}>
                                <span>Rating: {lp.rating || 'N/A'}</span>
                                <span>Reviews: {lp.reviews || 0}</span>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    ))
                  )}
                </div>
              )}

              {activeTab === 'trends' && (
                <div>
                  <h3 style={{margin: '0 0 16px', fontSize: '16px'}}>Ranking Trends</h3>
                  <p style={{color: '#666', fontSize: '14px', marginBottom: '20px'}}>Position changes over time. Track regularly for trend data.</p>
                  {rankData.rankings.map((r, i) => (
                    <div key={i} style={{display: 'flex', alignItems: 'center', gap: '16px', padding: '12px 0', borderBottom: '1px solid #eee'}}>
                      <div style={{flex: 2, fontWeight: 500}}>{r.keyword}</div>
                      <div style={{flex: 1, textAlign: 'center'}}>
                        <span style={{padding: '4px 12px', borderRadius: '20px', background: getRatingColor(r.position) + '20',
                          color: getRatingColor(r.position), fontWeight: 600, fontSize: '14px'}}>{r.position || 'N/R'}</span>
                      </div>
                      <div style={{flex: 1, textAlign: 'center'}}>
                        {r.change > 0 && <span style={{color: '#28a745', fontWeight: 600}}>+{r.change}</span>}
                        {r.change < 0 && <span style={{color: '#dc3545', fontWeight: 600}}>{r.change}</span>}
                        {(r.change === 0 || !r.change) && <span style={{color: '#999'}}>-</span>}
                      </div>
                      <div style={{width: '120px', height: '6px', background: '#e9ecef', borderRadius: '3px'}}>
                        <div style={{width: Math.min(100, r.position ? Math.max(5, 100 - (r.position - 1) * 5) : 0) + '%', height: '100%', background: getRatingColor(r.position), borderRadius: '3px'}}></div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </>
      )}

      {!rankData && !loading && (
        <div style={{...cardStyle, textAlign: 'center', padding: '60px 30px'}}>
          <div style={{fontSize: '48px', marginBottom: '16px'}}>\uD83D\uDCCD</div>
          <h3 style={{margin: '0 0 8px', color: '#333'}}>Start Tracking Local Rankings</h3>
          <p style={{color: '#666', maxWidth: '500px', margin: '0 auto'}}>
            Enter your business domain, target location, and keywords to monitor your local search visibility in Google Search and Maps.
          </p>
        </div>
      )}
    </div>
      <FAQSection items={LOCAL_FAQ} />
    </>
  );
};

export default LocalRankings;
